import React, { createContext, useContext, useState, useCallback } from "react";

const _envApiUrl = import.meta.env.VITE_API_URL;
const API_BASE: string =
  _envApiUrl !== undefined
    ? _envApiUrl
    : typeof window !== "undefined" && window.location.hostname === "localhost"
      ? "http://localhost:3001"
      : "";

export interface SuperAdmin {
  id: number;
  email: string;
  name: string;
}

export interface ImpersonationSession {
  token: string;
  sessionId: number;
  accountId: number;
  role: string;
  tenantSlug: string;
  tenantName: string;
  expiresAt: string;
  userName: string;
  // Regular app JWT + user object — written to tenant localStorage so AuthContext
  // treats this as a valid session and the app renders without hitting the login screen.
  appJwt?: string;
  appUser?: Record<string, any>;
}

interface SuperAdminContextType {
  superAdmin: SuperAdmin | null;
  token: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  impersonation: ImpersonationSession | null;
  startImpersonation: (session: ImpersonationSession) => void;
  stopImpersonation: () => void;
}

const SuperAdminContext = createContext<SuperAdminContextType | null>(null);

const LS_TOKEN = "besthr_sa_token";
const LS_ADMIN = "besthr_sa_user";
const LS_IMP = "besthr_sa_impersonation";

export function SuperAdminProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(LS_TOKEN));
  const [superAdmin, setSuperAdmin] = useState<SuperAdmin | null>(() => {
    const stored = localStorage.getItem(LS_ADMIN);
    if (stored) {
      try { return JSON.parse(stored); } catch { return null; }
    }
    return null;
  });
  const [impersonation, setImpersonation] = useState<ImpersonationSession | null>(() => {
    const stored = localStorage.getItem(LS_IMP);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ImpersonationSession;
        // Discard if already expired
        if (new Date(parsed.expiresAt) <= new Date()) return null;
        return parsed;
      } catch { return null; }
    }
    return null;
  });

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/superadmin/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, error: data.error || "Login failed" };
      }
      setToken(data.token);
      setSuperAdmin(data.superAdmin);
      localStorage.setItem(LS_TOKEN, data.token);
      localStorage.setItem(LS_ADMIN, JSON.stringify(data.superAdmin));
      return { success: true };
    } catch {
      return { success: false, error: "Network error" };
    }
  }, []);

  const logout = useCallback(() => {
    if (token) {
      fetch(`${API_BASE}/api/superadmin/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    setToken(null);
    setSuperAdmin(null);
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_ADMIN);
    // Clear any active impersonation on logout
    setImpersonation(null);
    localStorage.removeItem(LS_IMP);
  }, [token]);

  const startImpersonation = useCallback((session: ImpersonationSession) => {
    // Set the active tenant FIRST so tenantStorageKey() resolves correctly
    localStorage.setItem("besthr_active_tenant", session.tenantSlug);

    // Inject a real app JWT + user object into the tenant-scoped keys that
    // AuthContext reads on load — this makes the app bypass the login screen
    // and render the dashboard directly as the impersonated user.
    if (session.appJwt) {
      localStorage.setItem(`besthr_${session.tenantSlug}_token`, session.appJwt);
    }
    if (session.appUser) {
      localStorage.setItem(`besthr_${session.tenantSlug}_user`, JSON.stringify(session.appUser));
    }

    localStorage.setItem(LS_IMP, JSON.stringify(session));
    setImpersonation(session);
  }, []);

  const stopImpersonation = useCallback(() => {
    if (impersonation) {
      // Revoke the session server-side (best-effort)
      fetch(`${API_BASE}/api/superadmin/accounts/${impersonation.accountId}/impersonate/${impersonation.sessionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
      // Clean up injected tenant credentials
      localStorage.removeItem(`besthr_${impersonation.tenantSlug}_token`);
      localStorage.removeItem(`besthr_${impersonation.tenantSlug}_user`);
    }
    setImpersonation(null);
    localStorage.removeItem(LS_IMP);
    localStorage.removeItem("besthr_active_tenant");
  }, [impersonation, token]);

  return (
    <SuperAdminContext.Provider value={{
      superAdmin, token, login, logout, isAuthenticated: !!token,
      impersonation, startImpersonation, stopImpersonation,
    }}>
      {children}
    </SuperAdminContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSuperAdmin() {
  const ctx = useContext(SuperAdminContext);
  if (!ctx) throw new Error("useSuperAdmin must be used within SuperAdminProvider");
  return ctx;
}

export interface Account {
  id: number;
  name: string;
  slug: string;
  schema_name: string;
  status: "active" | "suspended" | "pending";
  logo_url: string | null;
  primary_color: string | null;
  contact_email: string | null;
  max_users: number | null;
  notes: string | null;
  enabled_modules: Record<string, boolean> | null;
  created_at: string;
  updated_at: string;
}

// Fetch helper with Bearer token
// eslint-disable-next-line react-refresh/only-export-components
export function useSuperAdminFetch() {
  const { token, logout } = useSuperAdmin();
  const API_BASE_URL = API_BASE;

  return useCallback(
    async <T,>(path: string, options?: RequestInit): Promise<T> => {
      const res = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...((options?.headers as Record<string, string>) || {}),
        },
      });
      if (res.status === 401) {
        logout();
        throw new Error("Session expired");
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      return data as T;
    },
    [token, logout, API_BASE_URL]
  );
}
